<template>
  <v-dialog v-model="isOpen"
    persistent
    scrollable
    fullscreen
    content-class="overlay-browser">
    <v-card>
      <multiview-browser-header text="Image Overlay" @closeModal="closeModal" />
      <v-card-text class="fill-height">
        <v-container class="pa-0" fluid fill-height>
          <v-layout wrap
            class="align-content-start"
            @wheel="updateRadius">
            <v-flex xs12>
              <overlay-toolbar
                :transform="transform"
                :radius="radius"
                :periscope="usePeriscope"
                :opacity="opacity"
                @togglePeriscope="togglePeriscope"
                @updateZoom="updateZoom"
                @updateRadius="radius = $event"
                @updateOpacity="updateOpacity"
                @addImages="$store.dispatch('closeOverlayToAddImages')"/>
            </v-flex>
            <v-flex xs2 class="overlay-browser-full-column border-right">
              <overlay-image-drawer
                drawerId="foreground-thumbnail-drawer"
                :images="images"
                :colorSettingOptions="colorSettingOptions"
                :colorSettings="foregroundSettings"
                :selectedImage="foreground"
                :size="thumbnailSize"
                @selectRecord="selectRecord($event, true)"/>
            </v-flex>
            <v-flex
              xs8
              class="overlay-browser-full-column overlay-main-view interactive-canvas-wrapper"
              id="overlay-main-view"
              @click.self.left="handleClick"
              @mousemove.self="handleMousemove"
              v-hammer:tap="handleTap"
              v-hammer:pan="handlePanmove">
              <pixel-shaded-canvas v-if="isOpen"
                :img="backgroundImg"
                :colorSettings="backgroundSettings"
                :canvasSize="fit"
                :imgSize="fit"
                :hires="true"
                :transform="transform"/>
              <pixel-shaded-canvas v-if="isOpen"
                :img="foregroundImg"
                :colorSettings="foregroundSettings"
                :canvasSize="fit"
                :imgSize="fit"
                :hires="true"
                :transform="transform"
                :style="opacityStyle"/>
              <canvas-annotation v-if="isOpen"
                v-show="usePeriscope"
                :canvasSize="fit"
                :imgSize="fit"
                :transform="transform"
                :annotation="periscope"/>
            </v-flex>
            <v-flex xs2 class="overlay-browser-full-column border-left">
              <overlay-image-drawer
                drawerId="background-thumbnail-drawer"
                :images="images"
                :colorSettingOptions="colorSettingOptions"
                :colorSettings="backgroundSettings"
                :selectedImage="background"
                :size="thumbnailSize"
                @selectRecord="selectRecord($event, false)"/>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import multiviewBrowserHeader from "./MultiviewBrowserHeader";
import overlayToolbar from "./OverlayToolbar";
import overlayImageDrawer from "./OverlayImageDrawer";
import responsiveCanvases from "../mixins/ResponsiveCanvases";
import pixelShadedCanvas from "../../canvas/components/PixelShadedCanvas";
import canvasAnnotation from "../../canvas/components/CanvasAnnotation";
import Periscope from "../../canvas/annotations/periscope";
import touchMouseEvents from "../mixins/TouchMouseEvents";
import { mapGetters } from "vuex";
import { mapWatchers } from "../../helpers/utils";
import { defaultColorSettings } from "../../canvas/canvasUtils";
import { defaultSize, getViewSize, setSize, setFittedSize, defaultTransform, setOffsets, setZoom, viewOffset, 
  withinViewBounds, setTransformSizes, oldCalculateRealLocation } from "../../canvas/geometry";
export default {
  name: "OverlayBrowser",
  mixins: [responsiveCanvases, touchMouseEvents],
  components: {
    multiviewBrowserHeader,
    overlayToolbar,
    overlayImageDrawer,
    pixelShadedCanvas,
    canvasAnnotation
  },
  data() {
    return {
      thumbnailSize: defaultSize(),
      foregroundSettings: defaultColorSettings(),
      backgroundSettings: defaultColorSettings(),
      mainViewSize: defaultSize(),
      fit: defaultSize(),
      transform: defaultTransform(),
      opacity: 1.0,
      radius: 100,
      showBackground: false,
      periscope: new Periscope(),
      usePeriscope: true
    };
  },
  computed: {
    ...mapGetters(["overlayContext"]),
    opacityStyle() {
      return {
        opacity: this.opacity
      };
    },
    isOpen() {
      return !!this.overlayContext;
    },
    images() {
      return (this.overlayContext && this.overlayContext.images) || [];
    },
    foreground() {
      return this.overlayContext && this.overlayContext.foreground;
    },
    foregroundImg() {
      return (
        this.foreground &&
        (this.foreground.imgHtmlObject || this.foreground.thumbHtmlObject)
      );
    },
    foregroundImgSize() {
      return (
        this.foreground &&
        (this.foreground.imgSize || this.foreground.thumbSize)
      );
    },
    background() {
      return this.overlayContext && this.overlayContext.background;
    },
    backgroundImg() {
      return (
        this.background &&
        (this.background.imgHtmlObject || this.background.thumbHtmlObject)
      );
    },
    backgroundImgSize() {
      return (
        this.background &&
        (this.background.imgSize || this.background.thumbSize)
      );
    },
    colorSettingOptions() {
      return this.overlayContext && this.overlayContext.colorSettingOptions;
    },
    imgOffset() {
      return viewOffset(this.fit, this.fit);
    }
  },
  methods: {
    updateCanvasSizes() {
      let drawerId = "foreground-thumbnail-drawer";
      let ftds = getViewSize(drawerId);
      let w = ftds.w - 50;
      let aspectFit = { w, h: w * 0.8 };
      setSize(this.thumbnailSize, { w: w, h: Math.min(aspectFit.h, 200) });

      if (!this.foregroundImgSize) return;
      let mainId = "overlay-main-view";
      this.mainViewSize = getViewSize(mainId);
      setFittedSize(this.mainViewSize, this.foregroundImgSize, this.fit, 15);

      setTransformSizes(this.transform, this.fit, this.fit);
      setOffsets(this.transform);
    },
    closeModal() {
      this.$store.dispatch("closeOverlay");
    },
    selectRecord({ image, settings }, isForeground) {
      if (isForeground) {
        this.overlayContext.selectForeground(image);
        this.foregroundSettings = settings;
      } else {
        this.overlayContext.selectBackground(image);
        this.backgroundSettings = settings;
      }
    },
    resetTransform() {
      this.transform = defaultTransform();
    },
    updateZoom(zoom) {
      setZoom(this.transform, zoom);
      setOffsets(this.transform);
    },
    realLocation(x, y) {
      if (!withinViewBounds(x, y, this.mainViewSize, this.imgOffset))
        return { s: -1, y: -1 };
      return oldCalculateRealLocation(x, y, this.imgOffset, this.transform, this.mainViewSize, this.fit);
    },
    isInBounds(x, y) {
      return x >= 0 && y >= 0 && x <= this.fit.w && y <= this.fit.h;
    },
    handleInteractionStart(e){
      const { x, y } = this.realLocation(e.offsetX, e.offsetY);
      if (!this.isInBounds(x, y)) return;
      if (!this.usePeriscope) return;
      this.periscope.click();
    },
    handleHover(e){
      if (!this.usePeriscope) return;
      this.handleInteractionMove(e);
    },
    handleInteractionMove(e){
      const { x, y } = this.realLocation(e.offsetX, e.offsetY);
      if (!this.isInBounds(x, y)) return;
      if (!this.usePeriscope) return;
      this.periscope.mousemove(x, y);
    },
    updateRadius(e){
      this.radius = Math.max(100, Math.min(500, this.radius - e.deltaY / 3));
    },
    updateOpacity(e){
      this.opacity = e;
    },
    togglePeriscope() {
      this.usePeriscope = !this.usePeriscope;
    }
  },
  watch: {
    ...mapWatchers(["isOpen", "foregroundImgSize"], function() {
      this.resetView();
      this.updateCanvasSizes();
    }),
    ...mapWatchers(["foregroundImg", "backgroundImg"], function() {
      this.resetView();
      this.resetTransform();
      this.updateCanvasSizes();
    }),
    backgroundImg() {
      this.periscope.updateImg(this.backgroundImg);
    },
    radius() {
      this.periscope.updateRadius(this.radius);
    },
    fit() {
      this.periscope.updateImgSize(this.fit);
    },
    backgroundSettings(){
      this.periscope.updateColorSettings(this.backgroundSettings);
    }
  },
  mounted() {
    this.periscope.updateImg(this.backgroundImg);
    this.periscope.updateImgSize(this.fit);
    this.periscope.updateRadius(this.radius);
    this.periscope.updateColorSettings(this.backgroundSettings);
    this.updateCanvasSizes();
  }
};
</script>